import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    IAccountMappingTemplateGroup,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { debounceTime } from 'rxjs';
import {
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    USER_INPUT_DEBOUNCE_TIME,
} from '../../../../../../../aumico-lib-core-fe/src/lib';
import { MappingItemsSelectionNode } from '../../../../interfaces/mapping-items-selection-node.interface';
import { SuperTableMapper } from '../../../../mappers/super-table.mapper';
@UntilDestroy()
@Component({
    selector: 'mapping-items-selection',
    templateUrl: 'mapping-items-selection.component.html',
    styleUrls: ['mapping-items-selection.component.scss'],
})
export class MappingItemsSelectionComponent implements OnChanges {
    searchForm: FormGroup | undefined;
    filteredMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    selectedItems: MappingItemsSelectionNode[] = [];
    dropdownOpen = false;

    @Input() mulitpleSelection = false;

    @Input() sameNodeTypeSelection = true;

    @Input() set mappingItemsSelectionTree(value: MappingItemsSelectionNode | undefined) {
        this.filteredMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(value);
    }

    @Input() existingSelectionItemsNodes: MappingItemsSelectionNode[] = [];

    @Input() language: string | undefined;

    @Output() onSelectedItemsChange: EventEmitter<MappingItemsSelectionNode[]> = new EventEmitter<
        MappingItemsSelectionNode[]
    >();

    constructor(private readonly superTableMapper: SuperTableMapper) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['existingSelectionItemsNodes']?.currentValue != null) {
            this.selectedItems = cloneDeep<MappingItemsSelectionNode[]>(this.existingSelectionItemsNodes);
            this.onSelectedItemsChange.emit(this.selectedItems);
        }
    }

    ngOnInit(): void {
        this.searchForm = new FormGroup({
            searchTerm: new FormControl(''),
        });

        // Open dropdown if no selection exists
        this.dropdownOpen = this.existingSelectionItemsNodes?.length === EMPTY_ARRAY_LENGTH;

        this.searchForm.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe((value) => {
                this.filterRootNode(this.filteredMappingItemsSelectionTree, value.searchTerm);
            });
    }

    openDropdown(): void {
        this.dropdownOpen = true;
    }

    onAnyKey(event: KeyboardEvent): void {
        if (event.key !== 'Escape') {
            this.openDropdown();
        } else {
            this.closeDropdown();
        }
    }

    closeDropdown(): void {
        // Timeout to allow click action to fire before close
        setTimeout(() => {
            this.dropdownOpen = false;
        }, 150);
    }

    onToggleSelect(selectedNode: MappingItemsSelectionNode): void {
        if (!this.mulitpleSelection) {
            this.handleSingleSelection(selectedNode);
            return;
        }

        if (this.mulitpleSelection) {
            this.handleMultipleSelection(selectedNode);
            return;
        }
    }

    private handleSingleSelection(selectedNode: MappingItemsSelectionNode): void {
        // Ensure only one parent is selected at a time
        if (
            this.selectedItems == null ||
            this.selectedItems.length === EMPTY_ARRAY_LENGTH ||
            this.superTableMapper.getNodeIdentifier(this.selectedItems[FIRST_ELEMENT_INDEX]) !==
                this.superTableMapper.getNodeIdentifier(selectedNode)
        ) {
            // Deselect the previous selection
            if (this.selectedItems?.[FIRST_ELEMENT_INDEX]) {
                this.deselectAllNodes(this.selectedItems[FIRST_ELEMENT_INDEX]);
            }

            // Set new selection
            this.selectedItems = [selectedNode];
            this.onSelectedItemsChange.emit(this.selectedItems);
            selectedNode.selected = true;
            this.selectAllChildren(selectedNode);
        } else {
            // If the same parent is selected again, deselect it
            this.deselectAllNodes(selectedNode);
            this.selectedItems = null;
            this.onSelectedItemsChange.emit([]);
        }
    }

    private handleMultipleSelection(selectedNode: MappingItemsSelectionNode): void {
        //Ensure only Multiple Selections of the same type
        if (
            this.sameNodeTypeSelection &&
            this.selectedItems.length !== EMPTY_ARRAY_LENGTH &&
            this.selectedItems[FIRST_ELEMENT_INDEX].type !== selectedNode.type &&
            !this.superTableMapper.isParentNode(this.selectedItems[FIRST_ELEMENT_INDEX], selectedNode)
        ) {
            return;
        }
        if (this.superTableMapper.isParentNode(this.selectedItems[FIRST_ELEMENT_INDEX], selectedNode)) {
            if (this.selectedItems && this.selectedItems.length) {
                for (const item of this.selectedItems) {
                    try {
                        this.deselectAllNodes(item);
                    } catch (e) {}
                }
            }
            // Clear the selectedItems array and notify listeners
            this.selectedItems = [];
            this.onSelectedItemsChange.emit(this.selectedItems);
        }

        if (
            this.selectedItems.find(
                (item) =>
                    this.superTableMapper.getNodeIdentifier(item) ===
                    this.superTableMapper.getNodeIdentifier(selectedNode),
            ) == null
        ) {
            // Set new selection
            this.selectedItems.push(selectedNode);
            this.onSelectedItemsChange.emit(this.selectedItems);
            selectedNode.selected = true;
            this.selectAllChildren(selectedNode);
        } else {
            // If the same parent is selected again, deselect it
            this.deselectAllNodes(selectedNode);
            this.selectedItems = this.selectedItems.filter((item) => !fastDeepEqual(item.data, selectedNode.data));
            this.onSelectedItemsChange.emit(this.selectedItems);
        }
    }

    selectAllChildren(node: MappingItemsSelectionNode) {
        this.filteredMappingItemsSelectionTree = this.superTableMapper.selectMatchingNode(
            node,
            this.filteredMappingItemsSelectionTree,
        );
    }

    deselectNode(node: MappingItemsSelectionNode): void {
        this.deselectAllNodes(node);

        this.selectedItems = this.selectedItems.filter(
            (item) => this.superTableMapper.getNodeIdentifier(item) !== this.superTableMapper.getNodeIdentifier(node),
        );

        this.onSelectedItemsChange.emit(this.selectedItems);
    }

    deselectAllNodes(node: MappingItemsSelectionNode) {
        this.filteredMappingItemsSelectionTree = this.superTableMapper.selectMatchingNode(
            node,
            this.filteredMappingItemsSelectionTree,
            true,
        );
    }

    deselectAll(): void {
        this.filteredMappingItemsSelectionTree = this.superTableMapper.selectMatchingNode(
            this.filteredMappingItemsSelectionTree,
            this.filteredMappingItemsSelectionTree,
            true,
        );
        this.selectedItems = [];
    }

    resetFilter(): void {
        this.searchForm.controls['searchTerm'].setValue('');
    }

    private filterRootNode(root: MappingItemsSelectionNode, searchTerm: string): MappingItemsSelectionNode | null {
        const isRootVisible = this.filterSubtree(root, searchTerm);
        return isRootVisible ? root : null;
    }

    /**
     * Recursively determines whether `node` or any of its children match
     * the search term. Sets `node.visible` accordingly.
     *
     * @returns `true` if this node should remain visible (itself or children matched),
     *          `false` otherwise.
     */
    private filterSubtree(node: MappingItemsSelectionNode, searchTerm: string): boolean {
        // 1. Check if this node itself matches
        const isMatch = this.matchesSearch(node.data, searchTerm);

        // 2. Recursively filter children
        let anyChildVisible = false;
        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                const childVisible = this.filterSubtree(child, searchTerm);
                if (childVisible) {
                    anyChildVisible = true;
                }
            }
        }

        // 3. Node is visible if it matches itself or has any visible descendant
        const nodeVisible = isMatch || anyChildVisible;
        node.visible = nodeVisible;

        // 4. Expand nodes that has visible children
        if (!isMatch && anyChildVisible) {
            node.expanded = true;
        } else if (!searchTerm) {
            node.expanded = true;
        } else {
            node.expanded = false;
        }

        return nodeVisible;
    }

    private matchesSearch(
        data: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem | AccountMappingAccountItem,
        term: string,
    ): boolean {
        if (!data) return false;
        // If the search string is empty, show everything:
        if (!term) return true;

        const lowerTerm = term.toLowerCase();
        const titleMatches = data.title?.toLowerCase().includes(lowerTerm) ?? false;
        const captionMatches = data.caption?.toLowerCase().includes(lowerTerm) ?? false;
        const nameMatches = data.name?.toLowerCase().includes(lowerTerm) ?? false;
        const accountMatches = data.accountNumber?.toLowerCase().includes(lowerTerm) ?? false;

        return titleMatches || captionMatches || nameMatches || accountMatches;
    }
}
